import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Input,
  Spinner
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import axiosInstance from "utils/axiosInstance";

// Select and DND
import Select, { components } from 'react-select';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

// Custom CSS
import "./InfoThemeEditor.css";

// Common Components
import ThumbnailUploader from './components/ThumbnailUploader';
import StatusSelect from "./components/StatusSelect";
import HorizontalLoader from 'components/Common/HorizontalLoader';
import AnimatedTextHighlight from "components/Common/AnimatedTextHighlight";
import AnimatedTextBackground from "assets/images/components/AnimatedTextHighlight/AnimatedTextBackground.png";

// Custom Hooks and utils
import { useThumbnail } from 'utils/thumbnails/useThumbnail';
import { fetchThumbnailsWithCache } from "utils/thumbnails/fetchThumbnailsWithCache";
import { useEditableThemeFields } from './utils/useEditableThemeFields';

// Placeholders
import placeholderSmall from "assets/images/info/card-placeholder-small.png";

/*----------------------
/* Main Component
/*----------------------*/

const InfoThemeEditor = ({ t }) => {

  const navigate = useNavigate();
  const { infoThemeId } = useParams();

  //--------------------------------------------------
  // States
  //--------------------------------------------------
  const [openSelectId, setOpenSelectId] = useState(null);

  const [infoTheme, setInfoTheme] = useState(null);
  const [infoCollections, setInfoCollections] = useState([]);
  /*
  const [infoCollections, setInfoCollections] = useState([
    { _id: '1', order: 1, label: "" },
    { _id: '2', order: 2, label: "" },
    { _id: '3', order: 3, label: "" },
  ]);
  */
  const [infoThemeLoading, setInfoThemeLoading] = useState(true);
  const [error, setError] = useState(null);

  const [collectionStats, setCollectionStats] = useState({});

  //--------------------------------------------------
  // Fetch on mount
  //--------------------------------------------------

  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const [sortedCollections, setSortedCollections] = useState([]);

  const fetchThemeAndCollections = async () => {
    try {
      setInfoThemeLoading(true);
      setError(null);

      // Load theme and collections in parallel
      const [themeResponse, collectionsResponse] = await Promise.all([
        axiosInstance.get(`/knovia/infoTheme/${infoThemeId}`),
        axiosInstance.get(`/knovia/infoTheme/${infoThemeId}/getInfoCollections`)
      ]);

      console.log("infoTheme Loaded:", themeResponse.data);
      console.log("infoCollections in Theme Loaded:", collectionsResponse.data);

      setInfoTheme(themeResponse.data);

      const sorted = [...collectionsResponse.data].sort((a, b) => a.order - b.order);
      setSortedCollections(sorted); // Save for later use
      setInfoCollections(sorted);

      // Set Initial Saved Status
      const initialSavedStatus = {};
      sorted.forEach(c => {
        initialSavedStatus[c._id] = true;
      });
      setIsSavedStatus(initialSavedStatus);

    } catch (err) {
      console.error(err);
      setError("Failed to load theme or collections.");
    } finally {
      setInfoThemeLoading(false);
    }
  };

  const fetchCompletionStats = async (collections) => {
    try {
      const statsPromises = collections.map(async (collection) => {
        try {
          const res = await axiosInstance.get(`/knovia/infoCollection/${collection._id}/getCompletionStats`);
          return { id: collection._id, stats: res.data };
        } catch (err) {
          console.error(`Failed to fetch stats for ${collection._id}`, err);
          return { id: collection._id, stats: { total: 0, completed: 0 } }; // fallback
        }
      });
      const allStats = await Promise.all(statsPromises);
      const statsMap = {};
      allStats.forEach(({ id, stats }) => {
        statsMap[id] = stats;
      });
      setCollectionStats(statsMap);

    } catch (err) {
      console.error(err);
      setError("Failed to load collection completion stats.");
    }
  };

  const fetchThumbnails = async (collections) => {
    try {
      const thumbnailMap = await fetchThumbnailsWithCache(
        collections,
        "infoCollection",
        "sizeSmall",
        ({ id, url }) => {
          setThumbnailUrls(prev => ({
            ...prev,
            [id]: url
          }));
        },
      );
    } catch (err) {
      console.error("Thumbnail fetch failed:", err);
    }
  };

  useEffect(() => {
    fetchThemeAndCollections();
  }, [infoThemeId]);

  useEffect(() => {
    if (sortedCollections.length > 0) {
      // Slight delay to allow UI render first
      setTimeout(() => {
        fetchCompletionStats(sortedCollections);
        fetchThumbnails(sortedCollections);
      }, 0);
    }
  }, [sortedCollections]);


  //--------------------------------------------------
  // Editing Theme fields
  //--------------------------------------------------

  const {
    isSaving: isSavingLabel,
    isSaved: isSavedLabel,
    isError: isSavedLabelError,
    handleChange: handleThemeLabelChange,
    handleSave: handleSaveLabel
  } = useEditableThemeFields(infoTheme?.label, 'editLabel', 'label', infoThemeId, axiosInstance, setInfoTheme);

  const {
    isSaving: isSavingDescription,
    isSaved: isSavedDescription,
    isError: isSavedDescriptionError,
    handleChange: handleThemeDescriptionChange,
    handleSave: handleSaveDescription
  } = useEditableThemeFields(infoTheme?.description, 'editDescription', 'description', infoThemeId, axiosInstance, setInfoTheme);



  //--------------------------------------------------
  // ADD/DELETE Collections
  //--------------------------------------------------
  const handleAddCollection = async () => {
    try {

      const newCollectionPayload = {
        theme: infoThemeId,
        label: "New Collection"
      };

      const res = await axiosInstance.post("/knovia/infoCollection", newCollectionPayload);
      setInfoCollections((prev) => [...prev, res.data]);

    } catch (err) {
      console.error(err);
      setError("Failed to add collection.");
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    try {
      const deletedCollection = infoCollections.find((c) => c._id === collectionId);
      if (!deletedCollection) return;

      const deletedOrder = deletedCollection.order;

      // Send delete request to backend
      await axiosInstance.delete(`/knovia/infoCollection/${collectionId}`);

      // Update frontend state
      setInfoCollections((prev) => {
        const filtered = prev.filter((c) => c._id !== collectionId);
        return filtered.map((c) =>
          c.order > deletedOrder ? { ...c, order: c.order - 1 } : c
        );
      });

    } catch (err) {
      console.error(err);
      setError("Failed to delete collection.");
    }
  };


  
  //--------------------------------------------------
  // Status
  //--------------------------------------------------

  const [isSavingStatus, setIsSavingStatus] = useState({});
  const [isSavedStatus, setIsSavedStatus] = useState({});

  const statusOptions = [
    { value: 'needs_edit', label: '需要編輯' },
    { value: 'in_progress', label: '編輯中' },
    { value: 'finalized', label: '已完成' },
  ];

  const handleStatusChange = async (collectionId, newStatus) => {
    // Set saving to true for this specific collection
    setIsSavingStatus(prev => ({ ...prev, [collectionId]: true }));
    setIsSavedStatus(prev => ({ ...prev, [collectionId]: false }));

    try {
      const res = await axiosInstance.patch(`/knovia/infoCollection/${collectionId}/updateStatus`, {
        status: newStatus
      });

      const updatedBy = {
        name: res.data.lastModifiedBy?.username || 'Unknown',
        email: res.data.lastModifiedBy?.email || 'Unknown'
      };

      // Update local infoCollections with the new status
      setInfoCollections(prev =>
        prev.map(collection => 
          collection._id === collectionId 
            ? { 
                ...collection, 
                status: newStatus,
                lastStatusUpdate: {
                  updatedAt: new Date(res.data.lastUpdatedAt),
                  updatedBy
                }
              } 
            : collection
        )
      );

      // Mark as saved
      setIsSavedStatus(prev => ({ ...prev, [collectionId]: true }));

    } catch (err) {
      console.error('Failed to update status:', err);
      setError("Failed to update collection status.");
    } finally {
      // Clear saving status
      setIsSavingStatus(prev => ({ ...prev, [collectionId]: false }));
    }
  };


  //--------------------------------------------------
  // Collection List DND
  //--------------------------------------------------

  const [isSavingCollectionOrder, setIsSavingCollectionOrder] = useState(false);
  const [isSavedCollectionOrder, setIsSavedCollectionOrder] = useState(true);
  const [isSavedCollectionOrderError, setIsSavedCollectionOrderError] = useState(false);

  // Fires when dropping dragged items
  const handleDragEnd = (result) => {

    setIsSavedCollectionOrderError(false);
    setIsSavedCollectionOrder(false);
    // If there's no valid destination (e.g. dropped outside), do nothing
    if (!result.destination) return;

    // Reorder the array in state
    const newOrder = Array.from(infoCollections);
    // extract the moved item
    const [movedItem] = newOrder.splice(result.source.index, 1);
    // add movedItem to result.destination.index
    newOrder.splice(result.destination.index, 0, movedItem);

    setInfoCollections(newOrder);
    handleSaveCollectionOrder(newOrder);
  };

  // Saving new order
  const handleSaveCollectionOrder = async (newOrder) => {
    try {

      const payload = newOrder.map((q, index) => ({
        _id: q._id,
        label: q.label,
        order: index + 1,
      }));
      console.log('Saving collection order:', payload);
      setIsSavingCollectionOrder(true);

      await axiosInstance.patch(
        `/knovia/infoTheme/${infoThemeId}/reorderCollections`,
        payload
      );

      // Update local state after successful save
      const reorderedCollections = payload.map(({ _id, order }) => {
        const matchingCollection = infoCollections.find(c => c._id === _id);
        return { ...matchingCollection, order };
      });

      setInfoCollections(reorderedCollections.sort((a, b) => a.order - b.order));

      //alert("Collection order saved!");
      setIsSavingCollectionOrder(false);
      setIsSavedCollectionOrder(true);

    } catch (err) {
      console.error(err);
      setIsSavingCollectionOrder(false);
      setIsSavedCollectionOrderError(true);
      setError("Failed to save collection order.");
    }
  };



  //--------------------------------------------------
  // Thumbnails
  //--------------------------------------------------

  const original = useThumbnail("infoTheme", "uploadThumbnail", "viewThumbnail");
  const medium   = useThumbnail("infoTheme", "uploadThumbnail/sizeMedium", "viewThumbnail/sizeMedium");
  const small    = useThumbnail("infoTheme", "uploadThumbnail/sizeSmall", "viewThumbnail/sizeSmall");

  const handleFileChange = (e, uploadFn, size) => {
    const file = e.target.files[0];
    if (file) {
      uploadFn(file, infoTheme, axiosInstance, size);
    }
  };

  useEffect(() => {
    if (infoTheme?._id) {
      original.fetchThumbnail(infoTheme, axiosInstance, "sizeOriginal");
      medium.fetchThumbnail(infoTheme, axiosInstance, "sizeMedium");
      small.fetchThumbnail(infoTheme, axiosInstance, "sizeSmall");
    }
  }, [infoTheme?._id]);


  //--------------------------------------------------
  // Render Logic (Not in use)
  //--------------------------------------------------
  /*
  if (infoThemeLoading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!infoTheme) return <div>No InfoTheme found.</div>;
  */


  //--------------------------------------------------
  // Render
  //--------------------------------------------------
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>

              {/* Animated Texts */}
              <div className="d-flex justify-content-center mb-3">
                <div style={{ width: "fit-content" }}>
                  <AnimatedTextHighlight
                    text={"LIVE YOUR|MEDIEVAL |FANTASY"} 
                    backgroundImageUrl={AnimatedTextBackground}
                  />
                </div>
              </div>


              {/* Title and Button row */}
              <Row>
                <Col xs={4}>
                  <CardTitle className="mb-4">{t("編輯類別")}</CardTitle>
                </Col>
                <Col xs={8} style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                  <Button color="info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                    onClick={(e) => { navigate(`/editInfoThemeList`); }}
                  >
                    <i className='bx bx-arrow-back' ></i>{` 回到類別總表`}
                  </Button>
                  <Button color="primary" style={{ marginRight: "0.3rem" }}>
                    複製類別
                  </Button>
                </Col>
              </Row>


              {/* Edit Theme Info */}
              <Row style={{ padding: "1rem" }}>
                <Col xs={12}>

                  {/* Theme Label Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("類別名稱:", { defaultValue: "類別名稱:" })}</h4>
                      {isSavingLabel && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingLabel && !isSavedLabelError &&
                        <span style={{ color: isSavedLabel ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingLabel && isSavedLabelError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoTheme?.label || ""}
                        onChange={handleThemeLabelChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveLabel(infoTheme?.label)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>
                    
                  </Row>

                  {/* Theme Description Field */}
                  <Row className="mb-3 mb-md-2">

                    <Col xs={12} md={4} xl={3} className="d-flex align-items-center mb-2 mb-md-0">
                      <h4 className="mb-0 me-3">{t("類別描述:", { defaultValue: "類別描述:" })}</h4>
                      {isSavingDescription && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                      {!isSavingDescription && !isSavedDescriptionError &&
                        <span style={{ color: isSavedDescription ? "#34c38f" : "#aeabab" }}>✔ Saved</span>
                      }
                      {!isSavingDescription && isSavedDescriptionError &&
                        <span style={{ color: "red" }}>Error Saving Label!</span>
                      }
                    </Col>

                    <Col xs={8} md={6} xl={4} className="d-flex">
                      <Input className="me-3"
                        type="text"
                        value={infoTheme?.description || ""}
                        onChange={handleThemeDescriptionChange}
                      />
                      <Button color="outline-success" onClick={()=>{handleSaveDescription(infoTheme?.description)}}
                        style={{ padding: "7px 8px" }}
                      ><i className="bx bx-check" /></Button>
                    </Col>

                  </Row>


                  {/* Theme Thumbnails */}
                  <Row>
                    <Col xs={12}>
                      <h4>封面圖片:</h4>
                    </Col>
                  </Row>

                  <ThumbnailUploader
                    original={original}
                    medium={medium}
                    small={small}
                    handleFileChange={handleFileChange}
                  />


                  {/* Horizontal */}
                  <div className="d-flex d-md-none"
                    style={{
                      borderTop: "1px solid #e8e8e8",
                      margin: "30px 0 30px -36px",
                      width: "calc(100% + 72px)",
                    }}
                  />


                  {/* Collection Edit */}

                  <Row style={{ marginBottom: "0.5rem" }}>
                    <Col xs={12} style={{ display: "flex", justifyContent: "space-between", padding: "0" }}>

                      <Row style={{ width: "100%", margin: "0" }}>
                        <Col xs={12} md={4}>
                          <h3>類別中的主題集</h3>
                          {isSavingCollectionOrder && <span style={{ color: "#f1b34c" }}>Saving Order...</span>}
                          {!isSavingCollectionOrder && !isSavedCollectionOrderError &&
                            <span style={{ color: isSavedCollectionOrder ? "#34c38f" : "#aeabab" }}>✔ Saved Order</span>
                          }
                          {!isSavingCollectionOrder && isSavedCollectionOrderError &&
                            <span style={{ color: "red" }}>Error Saving Collection Order!</span>
                          }
                        </Col>
                        <Col xs={12} md={8} style={{ display:"flex", alignItems:"center", justifyContent:"flex-end" }}>
                          <Button color="outline-info" onClick={handleAddCollection} style={{ marginRight: "1rem" }}>
                            + 新增主題集
                          </Button>
                        </Col>
                      </Row>

                    </Col>
                  </Row>

                  {/* Collection Drag List */}
                  <Row>
                    <Col xs={12} style={{ padding: "0.5rem 3rem", border: "1px solid #bcbbbb", borderRadius: "5px", minHeight: "300px" }}>
                      {infoCollections.length === 0 ? (
                        <HorizontalLoader />
                      ) : (
                        <DragDropContext onDragEnd={handleDragEnd}>
                          <Droppable droppableId="collectionList">
                            {(provided) => (
                              <div
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                              >
                                {infoCollections.map((c, index) => (

                                  <Draggable key={c._id} draggableId={c._id} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                      >
                                        {/* Single row content */}
                                        <Row key={c._id} className="draggable-row mt-3 mb-4 mb-mt-0 mb-md-0">

                                          <Col xs={12} md={8} style={{ display: "flex", alignItems: "center", padding: "0.25rem 0rem" }}>

                                            <div style={{ minWidth: "75px", display: "flex", padding: "0px 15px 0px 0px" }}>
                                              <div
                                                style={{
                                                  height: '60px',
                                                  width: '60px',
                                                  borderRadius: '50%',
                                                  backgroundImage: `url(${thumbnailUrls[c._id] || placeholderSmall})`,
                                                  backgroundSize: 'cover',
                                                  backgroundPosition: 'center',
                                                  transition: 'background-image 0.7s ease-in-out',
                                                }}
                                              />
                                            </div>

                                            <Row style={{ width: "100%", height: "100%", margin: "0", border: "1px solid #bcbbbb", borderRadius: "5px" }}>
                                              <Col xs={12} style={{ padding: 0 }}>
                                                <div style={{ position: "relative", width: "100%", height: "100%", minHeight: "3rem", display: "flex", alignItems: "center" }}>
                                                  {/* Background Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    backgroundColor: "#f0f0f0",  // base gray
                                                    borderRadius: "5px",
                                                    zIndex: 0,
                                                  }} />

                                                  {/* Progress Bar */}
                                                  <div style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: `${collectionStats[c._id] ? (collectionStats[c._id].completed / collectionStats[c._id].total) * 100 : 0}%`,
                                                    height: "100%",
                                                    backgroundColor: "#d3eaff", // blue highlight
                                                    borderRadius: "5px 0 0 5px",
                                                    zIndex: 1,
                                                    transition: "width 1.2s ease-in-out"
                                                  }} />

                                                  {/* Text content */}
                                                  <div style={{
                                                    position: "relative",
                                                    zIndex: 2,
                                                    padding: "0.5rem 1rem",
                                                    width: "100%",
                                                  }}>
                                                    <p style={{ margin: 0, fontWeight: 500 }}>
                                                      {`${c.order}. ${c.label}`}{" "}
                                                      {collectionStats[c._id] && (
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 400 }}>
                                                          （{collectionStats[c._id].completed}/{collectionStats[c._id].total} 主題已完成編輯）
                                                        </span>
                                                      )}
                                                    </p>
                                                    {c.lastStatusUpdate && (
                                                      <div style={{ fontSize: "0.75rem", color: "#666", marginTop: "0.2rem", lineBreak: 'anywhere' }}>
                                                        最近狀態由 <strong>{c.lastStatusUpdate.updatedBy.name}</strong>（{c.lastStatusUpdate.updatedBy.email}）<br />
                                                        於 {new Date(c.lastStatusUpdate.updatedAt).toLocaleString()} 更新
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </Col>
                                            </Row>

                                          </Col>


                                          {/* Edit and Delete Collections */}
                                          <Col xs={4} sm={6} md={2} 
                                            className="d-flex align-items-center justify-content-end justify-content-sm-center"
                                          >
                                            <Button color="outline-info" style={{ padding: "7px 8px", marginRight: "0.3rem" }}
                                              onClick={(e) => { navigate(`/editInfoCollections/${c._id}`); }}
                                            >
                                              <i className="bx bxs-pencil" />
                                            </Button>

                                            <Button color="outline-danger" onClick={() => handleDeleteCollection(c._id)}>
                                              -
                                            </Button>
                                          </Col>

                                          {/* Collection Edit Status */}
                                          <Col xs={8} sm={6} md={2}
                                            className="d-flex flex-column justify-content-center align-items-center"
                                          >
                                            <div className="w-100 px-1 px-md-0">
                                              <StatusSelect
                                                value={c.status}
                                                onChange={(newStatus) => handleStatusChange(c._id, newStatus)}
                                                options={statusOptions}
                                                selectId={c._id}
                                                openSelectId={openSelectId}
                                                setOpenSelectId={setOpenSelectId}
                                              />
                                            </div>

                                            <div className="w-100 px-1 px-md-0">
                                              {isSavingStatus[c._id] && <span style={{ color: "#f1b34c" }}>Saving...</span>}
                                              {!isSavingStatus[c._id] && (
                                                <span style={{ color: isSavedStatus[c._id] ? "#34c38f" : "#aeabab" }}>
                                                  ✔ Saved!
                                                </span>
                                              )}
                                            </div>

                                          </Col>

                                        </Row>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}

                    </Col>
                  </Row>


                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(InfoThemeEditor);


